import {Col, Row} from "antd";
import React, {useCallback, useEffect, useState} from "react";
import {ShotLayers} from "./ShotLayers";
import {ShotViewport} from "./ShotViewport";
import {ElementProperties} from "./ElementProperties";
import {Character, Object, Shot, Combined, Narration} from "../../../feature/project/Shot";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import {updateCharacter, updateObject} from "../../../feature/project/shotSlice";
import {useParams} from "react-router-dom";
import {ElementInfo} from "./ElementInfo";
import {saveNarration} from "../../../feature/project/narrationSlice";
import {editorRoles, hasRoles, WithUserPermission} from "../../common/WithUserPermission";

export const ShotEditor = () => {
    const {projectId} = useParams()
    const dispatch = useAppDispatch()
    const [height, setHeight] = React.useState(0)
    const [width, setWidth] = React.useState(0)
    const shot = useAppSelector(state => state.shots.shot)
    const [element, setElement] = React.useState<Combined | undefined>(undefined)
    const [showElementToolbar, setShowElementToolbar] = React.useState(false)
    const [location, setLocation] = React.useState(false)
    const [info, setInfo] = React.useState({x: 0, y: 0, z: 0, scale: 1})
    const [scale, setScale] = useState(1)
    const configNarration = useAppSelector(state => state.projects.configNarration[0])
    const user = useAppSelector(state => state.users.currentUser)

    useEffect(() => {
        if (element?.type === 'Character') {
            const character = {
                ...shot?.characters?.find((character: Character) => character.id === element.id),
                type: 'Character'
            } as Combined
            setElement(character)
        }
        if (element?.type === 'Object') {
            const object = {
                ...shot?.objects?.find((object: Object) => object.id === element.id),
                type: 'Object'
            } as Combined
            setElement(object)
        }
        if (element?.type === 'Narrative') {
            const object = {
                ...shot?.narration,
                type: 'Narrative'
            } as Combined
            setElement(object)
        }
    }, [shot])

    useEffect(() => {
        !element ? setShowElementToolbar(false) : setShowElementToolbar(true)
        setInfo({x: element?.x || 0, y: element?.y || 0, z: element?.z || 0, scale: element?.scale || 1})
    }, [element])

    useEffect(() => {
        setScale(width > height ? width / 1920 : height / 1920)
    }, [width, height])

    const elementRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(node.clientWidth || 0)
            setHeight(node.clientHeight || 0)
        });
        resizeObserver.observe(node);
    }, []);

    return (

        <Row style={{height: "calc(100% - 20px)", marginTop: '20px'}}>
            <Col span={16}
                 style={{
                     position: "relative",
                     padding: 0,
                     margin: 0,
                     height: "calc(100% - 30px)",
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     background: '#eeeeee',
                     borderRadius: '6px',
                     clipPath: 'border-box',
                 }}
                 ref={elementRef}>

                <ShotViewport
                    editable={hasRoles(user, editorRoles)}
                    viewportSize={[height, width]}
                    shot={
                        {
                            ...shot,
                            objects: shot?.objects?.map((object) => {
                                return {
                                    ...object,
                                    type: 'Object'
                                }
                            }),
                            characters: shot?.characters?.map((character) => {
                                return {
                                    ...character,
                                    type: 'Character'
                                }
                            }),
                            narration: {
                                ...shot?.narration,
                                type: 'Narrative'
                            },
                        } as Shot
                    }
                    onElementSelected={(element: Combined | undefined) => {
                        setElement(element)
                        if (element) {
                            setLocation(false)
                        }
                    }}
                    onLocationSelected={() => {
                        setLocation(true)
                        setElement(undefined)
                    }}
                    onMove={(info) => setInfo(info)}
                    onElementSettingsClick={(show: boolean) => setShowElementToolbar(!show)}
                    element={element}
                    elementToolbar={
                        showElementToolbar ?
                            <>
                                <ElementProperties
                                    style={{
                                        zIndex: 100000,
                                        position: 'absolute',
                                        right: 10,
                                        top: 52,
                                        background: '#fff',
                                        borderRadius: 6,
                                        padding: 2,
                                        bottom: 10
                                    }}
                                    element={element}
                                    onScaleChanged={(value) => {
                                        setElement(
                                            {
                                                ...element,
                                                scale: value
                                            }
                                        )
                                    }}
                                    onTransformed={(element) => {
                                        if (projectId) {
                                            if (element.type === 'Character') {
                                                dispatch(updateCharacter({
                                                    projectId: parseInt(projectId),
                                                    character: element as Character
                                                }))
                                            }
                                            if (element.type === 'Object') {
                                                dispatch(updateObject({
                                                    projectId: parseInt(projectId),
                                                    object: element as Object
                                                }))
                                            }
                                            if (element.type === 'Narrative' && shot?.id) {
                                                dispatch(saveNarration({
                                                    projectId: parseInt(projectId),
                                                    shotId: shot.id,
                                                    narration: {
                                                        ...element as Narration,
                                                        x: configNarration.x,
                                                        y: configNarration.y,
                                                        width: configNarration.width,
                                                        height: configNarration.height,
                                                        z: configNarration.z,
                                                    }
                                                }))
                                            }
                                        }
                                    }}
                                    onAlignmentChanged={(alignment) => {
                                        if (projectId && shot?.id) {
                                            let x: number = (element as Narration).x ?? 0
                                            let y: number = (element as Narration).y ?? 0
                                            if (alignment.x === 0.5) {
                                                x = width / scale / 2 - ((element as Narration).width ?? 0) / 2
                                            }
                                            if (alignment.y === 0.5) {
                                                y = width / 1.77 / scale / 2 - ((element as Narration).height ?? 0) / 2
                                            }
                                            dispatch(
                                                saveNarration({
                                                    projectId: parseInt(projectId),
                                                    shotId: shot?.id,
                                                    narration: {
                                                        ...element as Narration,
                                                        x: x,
                                                        y: y
                                                    }
                                                })
                                            )
                                        }
                                    }}
                                />
                                <ElementInfo element={info}/>
                            </> : ''
                    }/>
            </Col>
            <Col span={8} style={{height: "100%", paddingLeft: 20}}>
                <ShotLayers
                    selectedElement={element}
                    onElementSelected={(element) => {
                        setElement(element)
                        setLocation(false)
                    }}
                    onLocationSelected={() => {
                        setLocation(true)
                        setElement(undefined)
                    }}
                    locationSelected={location}/>
            </Col>
        </Row>
    );
}